/* eslint-disable @typescript-eslint/no-explicit-any -- dynamic PostgREST/Supabase rows are shaped at runtime; typed at the boundary. TODO: replace with generated row types. */
/**
 * useContentDrafts — reads all Content Studio drafts with their slides and
 * captions so the Drafts tab can list them. Images live in B2 under
 * content/<draft_id>/<slide_index>.jpg; the frontend serves via /api/media/.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface DraftSlide {
  slide_index: number;
  prompt: string | null;
  image_r2_key: string | null;
  image_provider: string | null;
  image_generation_ms: number | null;
}

export type DraftStatus = 'draft' | 'prompt_ready' | 'image_ready' | 'caption_ready' | 'scheduled' | 'published' | 'failed' | 'archived';

export interface DraftRow {
  id: string;
  bucket_slug: string;
  product_id: string | null;
  status: DraftStatus;
  post_kind: 'single' | 'carousel' | 'story' | 'reel';
  caption: string | null;
  hashtags: string[];
  scheduled_at: string | null;
  published_at: string | null;
  windsor_media_id: string | null;
  publish_error: string | null;
  created_at: string;
  updated_at: string;
  slides: DraftSlide[];
}

async function fetchDrafts(): Promise<DraftRow[]> {
  const { data, error } = await marketing
    .from('content_draft')
    .select(`
      id, bucket_slug, product_id, status, post_kind, caption, hashtags,
      scheduled_at, published_at, windsor_media_id, publish_error,
      created_at, updated_at,
      slides:content_draft_slide(slide_index, prompt, image_r2_key, image_provider, image_generation_ms)
    `)
    .neq('status', 'archived')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  const rows = (data ?? []).map((d: any) => ({
    ...d,
    slides: (d.slides ?? []).sort((a: DraftSlide, b: DraftSlide) => a.slide_index - b.slide_index),
  })) as DraftRow[];
  // Hide drafts with no persisted images — they were composed before B2
  // persistence was wired and have nothing to preview or download.
  return rows.filter((d) => d.slides.some((s) => s.image_r2_key));
}

export function useContentDrafts() {
  return useQuery<DraftRow[]>({
    queryKey: ['content-drafts'],
    queryFn: fetchDrafts,
    staleTime: 15_000,
  });
}

export function useArchiveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (draftId: string) => {
      const { error } = await marketing
        .from('content_draft')
        .update({ status: 'archived' })
        .eq('id', draftId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['content-drafts'] }),
  });
}
